<template>
  <SectionView class="comparison-chart" :title="computedTitle">
    <div class="tab-container" v-if="showTabs">
      <div
        v-for="(tab, index) in computedTabs"
        :key="index"
        :class="['tab-item', { active: activeTabIndex === index }]"
        @click="switchTab(index)"
      >
        {{ tab }}
      </div>
    </div>

    <div class="legend-container">
      <div class="legend-item" v-if="activeTabIndex === 0">
        <span class="legend-dot disk"></span>
        <span class="legend-text">{{ legendTexts.excellentIndicator }}</span>
      </div>
      <div class="legend-item">
        <span class="legend-dot actual"></span>
        <span class="legend-text">{{ legendTexts.needAttention }}</span>
      </div>
      <div class="legend-item">
        <span
          :class="
            activeTabIndex === 0 ? 'legend-dot standard' : 'legend-dot disk'
          "
        ></span>
        <span v-if="activeTabIndex === 0" class="legend-text">{{ legendTexts.standard }}</span>
        <span v-else class="legend-text">{{ legendTexts.excellentIndicator }}</span>
      </div>
    </div>

    <div ref="chart" class="chart-container"></div>
  </SectionView>
</template>

<script>
/**
 * 对比柱状图组件
 * 用于展示实测值和标准值的对比
 */
import SectionView from "@/components/SectionView";
import ChartI18nHelper from '@/utils/ChartI18nHelper';

export default {
  name: "ComparisonBarChart",
  components: { SectionView },

  props: {
    title: {
      type: String,
      default: "",
    },
    // 图表数据
    data: {
      type: Array,
      required: true,
    },
    // 标签字段名
    labelKey: {
      type: String,
      default: "name",
    },
    // 实测值字段名
    actualValueKey: {
      type: String,
      default: "shice_value",
    },
    // 标准值字段名
    standardValueKey: {
      type: String,
      default: "bili_value",
    },
    valueKey: {
      type: String,
      default: "value",
    },
    // 是否显示标签页
    showTabs: {
      type: Boolean,
      default: true,
    },
    // 标签页选项
    tabs: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      chart: null,
      activeTabIndex: 0,
      // 图表国际化辅助类实例
      chartI18nHelper: null,
    };
  },

  computed: {
    // 计算标题
    computedTitle() {
      if (this.title) {
        return this.title;
      }
      return this.chartI18nHelper ? 
        this.chartI18nHelper.t('charts.elements.mainAssociationRatio') : 
        '主要关联比例';
    },
    
    // 计算标签页选项
    computedTabs() {
      if (this.tabs && this.tabs.length > 0) {
        return this.tabs;
      }
      if (!this.chartI18nHelper) {
        return ['营养关联', '内脏关联'];
      }
      return [
        this.chartI18nHelper.t('charts.elements.nutritionAssociation'),
        this.chartI18nHelper.t('charts.elements.organAssociation')
      ];
    },
    
    // 图例文本
    legendTexts() {
      if (!this.chartI18nHelper) {
        return {
          excellentIndicator: '优良指标',
          needAttention: '需要关注',
          standard: '标准数值'
        };
      }
      
      return {
        excellentIndicator: this.chartI18nHelper.t('charts.elements.excellentIndicator'),
        needAttention: this.chartI18nHelper.t('charts.elements.needAttention'),
        standard: this.chartI18nHelper.t('charts.elements.standard') + '数值'
      };
    }
  },

  mounted() {
    // 初始化图表国际化辅助类
    this.chartI18nHelper = new ChartI18nHelper(this.$i18n);
    
    this.initChart();

    // 监听窗口大小变化，重新渲染图表
    window.addEventListener("resize", this.resizeChart);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.resizeChart);
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
  },

  watch: {
    data: {
      handler() {
        this.updateChart();
      },
      deep: true,
    },
    activeTabIndex() {
      this.updateChart();
    },
    
    // 监听语言变化，自动更新显示内容
    '$i18n.locale': {
      handler() {
        // 重新初始化图表国际化辅助类
        this.chartI18nHelper = new ChartI18nHelper(this.$i18n);
        // 触发重新渲染
        this.$forceUpdate();
        // 更新图表
        this.$nextTick(() => {
          this.updateChart();
        });
      }
    }
  },

  methods: {
    initChart() {
      this.chart = this.$echarts.init(this.$refs.chart);
      this.updateChart();
    },

    resizeChart() {
      if (this.chart) {
        this.chart.resize();
      }
    },
    switchTab(index) {
      this.activeTabIndex = index;
      this.$emit("tab-change", index); // 触发标签切换事件，方便父组件监听
    },

    updateChart() {
      if (!this.chart || !this.data || this.data.length === 0) return;
      if (this.activeTabIndex === 0) {
        // 营养关联 - 使用原来的柱状图配置
        this.renderNutritionChart();
      } else {
        // 内脏关联 - 使用新的图表配置
        this.renderOrganChart();
      }
    },

    renderNutritionChart() {
      // 正常数值（蓝色）：只保留 >= 0 的数，其它为 null
      var normalValues = this.data.map((v) =>
        v.av >= 0 ? v[this.actualValueKey] : null
      );

      // 实测数值（红色）：只保留 < 0 的数，其它为 null
      var abnormalValues = this.data.map((v) =>
        v.av < 0 ? v[this.actualValueKey] : null
      );
      console.log("123", normalValues, abnormalValues);
      const labels = this.data.map((item) => item[this.labelKey]);
      const actualValues = this.data.map((item) => {
        // 将百分比字符串转换为数值
        const value = item[this.actualValueKey];
        if (typeof value === "string" && value.includes("%")) {
          return { ...item, value: parseFloat(value) / 100 };
        }
        return { ...item, value: parseFloat(value) };
      });
      // const actualValues = this.data.map((item) => {
      //   // 将百分比字符串转换为数值
      //   const value = item[this.actualValueKey];
      //   if (typeof value === "string" && value.includes("%")) {
      //     return parseFloat(value) / 100;
      //   }
      //   return parseFloat(value)||0 ;
      // });
      // console.log('actualValues',actualValues)

      const standardValues = this.data.map((item) => {
        // 将百分比字符串转换为数值
        const value = item[this.standardValueKey];
        if (typeof value === "string" && value.includes("%")) {
          return parseFloat(value) / 100;
        }
        return parseFloat(value) || 0;
      });

      const option = {
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "shadow",
          },
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          borderColor: "#eee",
          borderWidth: 1,
          padding: 15,
          textStyle: {
            color: "#333",
          },
          formatter: (params) => {
            const actualItem = params.find((p) => p.seriesName === "实测数值");
            const standardItem = params.find(
              (p) => p.seriesName === "标准数值"
            );
            
            const needAttentionText = this.chartI18nHelper ? 
              this.chartI18nHelper.t('charts.elements.needAttention') : '需要关注';
            const excellentIndicatorText = this.chartI18nHelper ? 
              this.chartI18nHelper.t('charts.elements.excellentIndicator') : '优良指标';
            const standardText = this.chartI18nHelper ? 
              this.chartI18nHelper.t('charts.elements.standard') : '标准';

            return `<div style="font-size:16px;color:#2C324E;font-weight:500;">
                        ${actualItem.name}
                      </div>
                      <div style="margin-top:10px;">
                        <span style="font-size:14px;color:#64749C;">${
                          actualItem.data.itemStyle.av<0? needAttentionText + "：" : excellentIndicatorText + "："
                        }</span>
                        <span style="float:right;font-size:14px;color:${actualItem.data.itemStyle.av<0? '#FF5C4D' : '#003ec6'};";font-weight:bold;">${
                          actualItem.value
                        }</span>
                      </div>
                      <div style="margin-top:5px;">
                        <span style="font-size:14px;color:#64749C;">${standardText}：</span>
                        <span style="float:right;font-size:14px;color:#C3C2C2;font-weight:bold;">${
                          standardItem.value
                        }</span>
                      </div>`;
          },
          extraCssText:
            "box-shadow: 0 0 10px rgba(0, 0, 0, 0.1); border-radius: 10px;",
        },
        grid: {
          top: 20,
          bottom: 30,
          left: 40,
          right: 20,
        },
        dataZoom: [
          {
            type: "inside",
            start: 0,
            end: Math.min(100, (6 / this.data.length) * 100),
            zoomLock: true,
            moveOnMouseWheel: true,
            moveOnMouseMove: true,
            preventDefaultMouseMove: true,
            throttle: 30,
            rangeMode: ["value", "value"],
          },
        ],
        xAxis: {
          type: "category",
          data: labels,
          axisLine: {
            lineStyle: {
              color: "#E8E8E8",
            },
          },
          axisTick: {
            show: false,
          },
          axisLabel: {
            interval: 0,
            fontSize: 13,
            color: "#959FB7",
            formatter: function (value) {
              if (value.length > 4) {
                return value.substring(0, 4) + "..";
              }
              return value;
            },
          },
        },
        yAxis: {
          type: "value",
          min: 0,
          max: 100,
          interval: 20,
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
          splitLine: {
            show: true,
            lineStyle: {
              type: "dashed",
              color: "#CCCCCC",
            },
          },
          axisLabel: {
            formatter: function (value) {
              return value + "%";
            },
          },
        },
        series: [
          {
            name: "实测数值", // 红色渐变柱子
            type: "bar",
            barWidth: "30%",
            barGap: "10%",
            data: actualValues.map((value) => {
              return {
                value: value.value,
                itemStyle: {
                  color: {
                    type: "linear",
                    x: 0,
                    y: 0,
                    x2: 0,
                    y2: 1,
                    colorStops:
                      value.av >= 0
                        ? [
                            { offset: 0, color: "#003ec6" },
                            { offset: 1, color: "#E0F3FF" },
                          ]
                        : [
                            { offset: 0, color: "#FF5C4D" },
                            { offset: 1, color: "#FFEAEA" },
                          ],
                  },
                  borderRadius: [50, 50, 0, 0],
                  av:value.av
                },
              };
            }),
            z: 10,
          },
          {
            name: "标准数值",
            type: "bar",
            barWidth: "30%",
            barGap: "10%",
            itemStyle: {
              color: {
                type: "linear",
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 1, color: "#FCFDFF" },
                  { offset: 0, color: "#D4D6DC" },
                ],
              },
              borderRadius: [50, 50, 0, 0],
            },
            data: standardValues,
            z: 5,
          },
        ],
      };

      this.chart.setOption(option);

      // 默认选中第一个柱状图
      if (this.data.length > 0) {
        this.chart.dispatchAction({
          type: "highlight",
          seriesIndex: 0,
          dataIndex: 0,
        });

        this.chart.dispatchAction({
          type: "showTip",
          seriesIndex: 0,
          dataIndex: 0,
        });
      }
    },
    renderOrganChart() {
      console.log("内脏关联数据:", this.data);
      const labels = this.data.map((item) => item[this.labelKey]);
      const values = this.data.map((item) => item[this.valueKey]);
      const axisRange = this.getOrganAxisRange(values);

      // 准备柱状图数据，包含渐变色和圆角设置
      const seriesData = values.map((value) => {
        if (value < 0) {
          // 负值柱状图：从 #FFF3F3 渐变到 #FF5C4D
          return {
            value,
            itemStyle: {
              color: {
                type: "linear",
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: "#FFF3F3" },
                  { offset: 1, color: "#FF5C4D" },
                ],
              },
              borderRadius: [0, 0, 50, 50],
            },
          };
        } else {
          // 正值柱状图：从 #FCFDFF 渐变到 #003CBF
          return {
            value,
            itemStyle: {
              color: {
                type: "linear",
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: "#003CBF" },
                  { offset: 1, color: "#FCFDFF" },
                ],
              },
              borderRadius: [50, 50, 0, 0],
            },
          };
        }
      });

      const option = {
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "shadow",
          },
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          borderColor: "#eee",
          borderWidth: 1,
          padding: 15,
          textStyle: {
            color: "#333",
          },
          formatter: (params) => {
            const item = params[0];
            const needAttentionText = this.chartI18nHelper ? 
              this.chartI18nHelper.t('charts.elements.needAttention') : '需要关注';
            const excellentIndicatorText = this.chartI18nHelper ? 
              this.chartI18nHelper.t('charts.elements.excellentIndicator') : '优良指标';
              
            return `<div style="font-size:15px;color:#2C324E;font-weight:500;">
                      ${item.name}
                    </div>
                    <div style="margin-top:10px;">
                      <span style="font-size:14px;color:#64749C;">${item.value<0? needAttentionText + '：' : excellentIndicatorText + '：'}</span>
                      <span style="float:right;font-size:14px;color:${item.value<0?'#FF5C4D':'#003ec6'};font-weight:bold;">${item.value}</span>
                    </div>`;
          },
          extraCssText:
            "box-shadow: 0 0 10px rgba(0, 0, 0, 0.1); border-radius: 10px; padding: 20px;",
        },
        grid: {
          top: 10,
          bottom: 30,
          left: 25,
          right: 15,
        },
        dataZoom: [
          {
            type: "inside",
            start: 0,
            // 根据数据长度动态调整结束位置
            end: Math.min(100, (6 / this.data.length) * 100),
            zoomLock: true,
            moveOnMouseWheel: true,
            moveOnMouseMove: true, // 允许鼠标移动时平移
            preventDefaultMouseMove: true, // 防止默认的鼠标移动事件
            throttle: 30, // 降低节流时间，提高响应速度
            rangeMode: ["value", "value"], // 使用数值模式
          },
          // {
          //   type: 'slider',
          //   show: this.data.length > 5,
          //   height: 20,
          //   bottom: 0,
          //   start: 0,
          //   end: this.data.length <= 5 ? 100 : 50
          // }
        ],
        xAxis: {
          type: "category",
          data: labels,
          axisLine: {
            lineStyle: {
              color: "#E8E8E8",
            },
          },
          axisTick: {
            show: false,
          },
          axisLabel: {
            interval: 0,
            fontSize: 12,
            color: "#959FB7",
            formatter: function (value) {
              // 如果文字太长，可以截断
              if (value.length > 3) {
                return value.substring(0, 3) + "..";
              }
              return value;
            },
          },
        },
        yAxis: {
          type: "value",
          min: axisRange.min,
          max: axisRange.max,
          interval: axisRange.interval,
          axisLine: {
            show: false,
            lineStyle: {
              color: "#CCCCCC",
            },
          },
          axisTick: {
            show: false,
          },

          splitLine: {
            show: true,
            lineStyle: {
              type: "dashed",
              color: "#CCCCCC",
            },
          },
          axisLabel: {
            formatter: "{value}",
          },
        },
        series: [
          {
            type: "bar",
            barWidth: "30%", // 单柱宽度（无需双柱间距）
            barMaxWidth: 30,
            // barGap: '10%',  // 双柱才需要，单柱删除此行
            data: seriesData, // 单柱数据（每个数据项是单个柱子
          },
        ],
      };

      this.chart.setOption(option, true);
      // 默认选中第一个柱状图
      if (this.data.length > 0) {
        this.chart.dispatchAction({
          type: "highlight",
          seriesIndex: 0,
          dataIndex: 0,
        });

        // 显示第一个柱状图的提示框
        this.chart.dispatchAction({
          type: "showTip",
          seriesIndex: 0,
          dataIndex: 0,
        });
      }
    },

    getOrganAxisRange(values) {
      const numericValues = values
        .map((value) => parseFloat(value))
        .filter((value) => !Number.isNaN(value));

      const maxAbs = Math.max(
        ...numericValues.map((value) => Math.abs(value)),
        0.2
      );
      const roughInterval = maxAbs / 2;
      const magnitude = Math.pow(10, Math.floor(Math.log10(roughInterval)));
      const normalizedInterval = roughInterval / magnitude;

      let niceInterval;
      if (normalizedInterval <= 1) {
        niceInterval = 1;
      } else if (normalizedInterval <= 2) {
        niceInterval = 2;
      } else if (normalizedInterval <= 5) {
        niceInterval = 5;
      } else {
        niceInterval = 10;
      }

      const interval = niceInterval * magnitude;
      const axisMax = Math.max(interval, Math.ceil(maxAbs / interval) * interval);

      return {
        min: -axisMax,
        max: axisMax,
        interval,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.comparison-chart {
  width: 100%;
  background-color: #f5f5f5;

  .tab-container {
    display: flex;
    border-bottom: 1px solid #f0f0f0;
    margin-bottom: 15px;

    .tab-item {
      flex: 1;
      text-align: center;
      padding: 15px 0;
      font-size: 16px;
      color: #959fb7;
      font-weight: 400;
      font-size: 31px;
      position: relative;
      cursor: pointer;

      &.active {
        color: #ff5c4d;
        font-weight: 500;

        &:after {
          content: "";
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 2px;
          background-color: #ff5c4d;
        }
      }
    }
  }

  .legend-container {
    display: flex;
    justify-content: flex-end;
    margin: 10px 0;

    .legend-item {
      display: flex;
      align-items: center;
      margin: 0 15px;

      .legend-dot {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        margin-right: 5px;

        &.actual {
          background-color: #ff5c4d;
        }

        &.standard {
          background-color: #c3c2c2;
        }
        &.disk {
          background-color: #003cbf;
        }
      }

      .legend-text {
        color: #00133d;
        font-weight: 400;
        font-size: 20px;
        color: #666;
      }
    }
  }

  .chart-container {
    margin-left: 20px;
    height: 450px;
    width: 100%;
  }
}
</style>
